/**
 * Each service is one page with tabs: its Overview (the service's dashboard, which the owner can
 * hide from a person) and its work. Old addresses (/tax/returns, /accounting/workflow…) open the
 * matching tab, so links and bookmarks keep working.
 */
import { useSearch } from "wouter";
import { ServiceFrame, type ServiceTab } from "@/components/page";
import { CloseGridPage, CreateClosesButton } from "@/components/books";
import { PayrollOverviewPage } from "@/components/payroll";
import { AdvisoryClientsPage } from "@/pages/staff/advisory";
import { TaxExtensionsPage, TaxRefundsPage } from "@/pages/staff/season";
import { TaxOverviewPage } from "@/pages/staff/tax-overview";
import { WorkspacePage } from "@/pages/staff/work";
import { AiReviewPage } from "@/pages/staff/ai";
import { DocumentReviewPage } from "@/pages/staff/document-review";
import { hasPermission, seesDashboard, type Me } from "@/lib/session";
import { ProposalsTab, RecurringTab } from "@/components/lifecycle";
import { BillingPage } from "@/pages/staff/season";

/** The tab to show: a hidden Overview opens the work instead. */
function pick(tabs: ServiceTab[], wanted: string) {
  return tabs.some((t) => t.key === wanted) ? wanted : tabs[0]!.key;
}

export type TaxTab = "overview" | "returns" | "extensions" | "refunds";

export function TaxServicePage({ me, tab }: { me: Me; tab: TaxTab }) {
  const tabs: ServiceTab[] = [
    ...(seesDashboard(me, "tax") ? [{ key: "overview", label: "Overview", href: "/tax" }] : []),
    { key: "returns", label: "Returns", href: "/tax/returns" },
    { key: "extensions", label: "Extensions", href: "/tax/extensions" },
    { key: "refunds", label: "Refund status", href: "/tax/refunds" },
  ];
  const active = pick(tabs, tab);
  return (
    <ServiceFrame title="Tax" description="The season at a glance, every return in its workflow, extensions filed and refunds to follow up." tabs={tabs} active={active}>
      {active === "overview" && <TaxOverviewPage />}
      {active === "returns" && <WorkspacePage module="tax" title="Tax Returns" me={me} />}
      {active === "extensions" && <TaxExtensionsPage />}
      {active === "refunds" && <TaxRefundsPage />}
    </ServiceFrame>
  );
}

export function AccountingServicePage({ me, tab }: { me: Me; tab: "overview" | "workflow" }) {
  const tabs: ServiceTab[] = [
    ...(seesDashboard(me, "bookkeeping") ? [{ key: "overview", label: "Month-end close", href: "/accounting" }] : []),
    { key: "workflow", label: "Workflow", href: "/accounting/workflow" },
  ];
  const active = pick(tabs, tab);
  return (
    <ServiceFrame title="Accounting Services" description="Where every client's month-end close stands, and the close work itself." tabs={tabs} active={active}>
      {active === "overview" ? <CloseGridPage me={me} /> : <WorkspacePage module="bookkeeping" title="Close Workflow" me={me} actions={<CreateClosesButton me={me} />} />}
    </ServiceFrame>
  );
}

export function PayrollServicePage({ me, tab }: { me: Me; tab: "overview" | "workflow" }) {
  const tabs: ServiceTab[] = [
    ...(seesDashboard(me, "payroll") ? [{ key: "overview", label: "Overview", href: "/payroll" }] : []),
    { key: "workflow", label: "Workflow", href: "/payroll/workflow" },
  ];
  const active = pick(tabs, tab);
  return (
    <ServiceFrame title="Payroll" description="Upcoming pay dates and cutoffs across clients, and the payroll work." tabs={tabs} active={active}>
      {active === "overview" ? <PayrollOverviewPage me={me} /> : <WorkspacePage module="payroll" title="Payroll Workflow" me={me} />}
    </ServiceFrame>
  );
}

export function AdvisoryServicePage({ me, tab }: { me: Me; tab: "overview" | "workflow" }) {
  const tabs: ServiceTab[] = [
    ...(seesDashboard(me, "advisory") ? [{ key: "overview", label: "Client dashboards", href: "/advisory" }] : []),
    { key: "workflow", label: "Workflow", href: "/advisory/workflow" },
  ];
  const active = pick(tabs, tab);
  return (
    <ServiceFrame title="CFO & Advisory" description="Each CFO client's figures at a glance, and the advisory work." tabs={tabs} active={active}>
      {active === "overview" ? <AdvisoryClientsPage /> : <WorkspacePage module="advisory" title="Advisory Workflow" me={me} />}
    </ServiceFrame>
  );
}

/** One review inbox: AI proposals waiting for approval, and documents the AI reader has read. */
export function ReviewPage({ me, tab, counts }: { me: Me; tab: "ai" | "documents"; counts: { ai?: number; documents?: number } }) {
  const n = (x?: number) => (x ? ` (${x})` : "");
  const tabs: ServiceTab[] = [
    { key: "ai", label: `AI proposals${n(counts.ai)}`, href: "/ai" },
    ...(hasPermission(me, "documents.read") ? [{ key: "documents", label: `Documents${n(counts.documents)}`, href: "/documents/review" }] : []),
  ];
  const active = pick(tabs, tab);
  // Opened from a client's record: the way back is to that client.
  const clientId = new URLSearchParams(useSearch()).get("clientId");
  return (
    <ServiceFrame title="Review" description="Everything waiting for a person: AI proposals to approve, and figures the AI reader took from documents to confirm." tabs={tabs} active={active} back={clientId ? { label: "the client", href: `/clients/${clientId}` } : undefined}>
      {active === "ai" ? <AiReviewPage me={me} /> : <DocumentReviewPage />}
    </ServiceFrame>
  );
}

/** Billing: invoices and receivables, recurring fees, and proposals (each client priced on its own). */
export function BillingServicePage({ me, tab }: { me: Me; tab: "invoices" | "recurring" | "proposals" }) {
  const tabs: ServiceTab[] = [
    { key: "invoices", label: "Invoices", href: "/billing" },
    { key: "recurring", label: "Recurring fees", href: "/billing/recurring" },
    { key: "proposals", label: "Proposals", href: "/billing/proposals" },
  ];
  return (
    <ServiceFrame title="Billing" description="Invoices and payments, recurring fees with automatic payment, and proposals with their engagement letter." tabs={tabs} active={tab}>
      {tab === "invoices" ? <BillingPage me={me} /> : tab === "recurring" ? <RecurringTab me={me} /> : <ProposalsTab me={me} />}
    </ServiceFrame>
  );
}
